import type {GetInboxThread_inboxThread as Thread} from 'nutshell-graphql-types';

import {Avatar} from '../../../avatar/avatar';
import {Card} from '../../../card/card';
import {colors} from '../../../colors/colors';
import {TextRegularAiIcon} from '../../../icon/svg/text-regular-ai-icon';
import {StackLegacy} from '../../../layout/stack/stack-legacy';
import {Body} from '../../../typography/body';

import {getApiTypeFromGraphQLTypename} from '../../../../../../../../core/utils/get-api-type-from-graphql-typename';

import {
    getThreadListItemHeaderIcon,
    getThreadListItemTimestamp,
    getRecipientFromThread,
    getRecipientIsOnline,
    getRecipientDisplayName,
    getThreadBottomDisplayContent,
    isUnidentifiedRecipient,
} from '../../helpers';
import {UnidentifiedChatAvatar} from '../../unidentified-chat-avatar';

import styles from './thread-list-item.module.css';

type Props = {
    thread: Thread;
    isSelected: boolean;
    onSelect: () => void;
    id?: string;
};

export function ThreadListItem(props: Props) {
    const recipient = getRecipientFromThread(props.thread);
    const threadImages = props.thread.displayMessage && props.thread.displayMessage.images;
    const hasImages = threadImages && Array.isArray(threadImages) && threadImages.length > 0;

    const renderMessageContent = () => {
        const hasSummary = Boolean(props.thread.threadSummary);

        let messageContent: string;
        if (props.thread.threadSummary) {
            messageContent = props.thread.threadSummary;
        } else if (props.thread.displayMessage) {
            messageContent = props.thread.displayMessage.content ?? '';
        } else {
            messageContent = '';
        }

        if (!hasSummary && hasImages && threadImages && threadImages.length > 0) {
            return (
                <div className={styles['message-with-images']}>
                    <div className={styles['image-preview-container']}>
                        <img
                            src={threadImages[0]}
                            alt='Message preview'
                            className={styles['image-preview']}
                        />
                        {threadImages.length > 1 && (
                            <div className={styles['image-count-badge']}>
                                +{threadImages.length - 1}
                            </div>
                        )}
                    </div>
                    {messageContent && (
                        <div className={styles['message-text']}>{messageContent}</div>
                    )}
                </div>
            );
        }

        return (
            <div className={styles.message}>
                {hasSummary && (
                    <span className={styles['summary-icon']}>
                        <TextRegularAiIcon
                            // @ts-expect-error
                            color={colors.aiPurplePrimary}
                            size={16}
                            wrapWithDiv={false}
                        />
                    </span>
                )}
                {messageContent}
            </div>
        );
    };

    return (
        <Card
            hasBorder={true}
            borderRadius={0}
            leftLineColor={props.isSelected ? colors.reportOrange : colors.white}
            backgroundColor={props.isSelected ? 'blueXlt' : 'white'}
            onClick={props.onSelect}
        >
            <div id={props.id} className={styles.content}>
                <StackLegacy spacing={8}>
                    <div className='flex justify-sb align-center'>
                        {recipient && (
                            <div className='flex align-center gap-8 min-width-0'>
                                <div className='flex align-center'>
                                    {isUnidentifiedRecipient(recipient.id) ? (
                                        <UnidentifiedChatAvatar
                                            type={getApiTypeFromGraphQLTypename(
                                                recipient.__typename
                                            )}
                                            size={24}
                                            isOnline={getRecipientIsOnline(props.thread)}
                                        />
                                    ) : (
                                        <Avatar
                                            type={getApiTypeFromGraphQLTypename(
                                                recipient.__typename
                                            )}
                                            avatarUrl={recipient.avatarUrl}
                                            // @ts-expect-error
                                            initials={recipient.initials}
                                            size={24}
                                            isOnline={getRecipientIsOnline(props.thread)}
                                        />
                                    )}
                                </div>
                                <Body
                                    isBold={props.isSelected}
                                    color='dark'
                                    truncate={{type: 'inline'}}
                                >
                                    {/* @ts-expect-error */}
                                    {getRecipientDisplayName(recipient, props.thread)}
                                </Body>
                            </div>
                        )}
                        {getThreadListItemHeaderIcon(props.thread)}
                    </div>
                    {renderMessageContent()}
                    <div className='flex justify-sb align-center'>
                        {getThreadBottomDisplayContent(props.thread)}
                        {props.thread.displayMessage &&
                            getThreadListItemTimestamp(props.thread.displayMessage)}
                    </div>
                </StackLegacy>
            </div>
        </Card>
    );
}
